import type {
  ArtifactSummary,
  ProjectSummary,
  SkillDefinition,
  SkillExecution,
} from '@rw/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { useI18n } from '../../../i18n/I18nProvider';
import { apiRequest } from '../../../lib/api';
import { useSkillExecutionUpdates } from '../../../lib/use-skill-execution-updates';
import { WEB_SEARCH_SKILL_ID } from '../constants';

type Translate = ReturnType<typeof useI18n>['t'];

type SkillsSnapshot = {
  artifacts: ArtifactSummary[];
  executions: SkillExecution[];
  projects: ProjectSummary[];
  skills: SkillDefinition[];
};

const skillsCache = new Map<string, SkillsSnapshot>();

function mergeProjectArtifacts(
  base: ArtifactSummary[],
  incoming: ArtifactSummary[]
): ArtifactSummary[] {
  const byId = new Map(base.map((artifact) => [artifact.id, artifact]));
  for (const artifact of incoming) {
    const current = byId.get(artifact.id);
    if (!current || artifact.updatedAt >= current.updatedAt) byId.set(artifact.id, artifact);
  }
  return [...byId.values()];
}

function skillsCacheKey(projectId: string | undefined, token: string): string {
  return `${token}:${projectId ?? ''}`;
}

export function resetChatSkillsCache(): void {
  skillsCache.clear();
}

export function useChatSkills({
  pendingSkillId = null,
  projectId,
  t,
  token,
}: {
  pendingSkillId?: string | null;
  projectId: string | undefined;
  t: Translate;
  token: string | undefined;
}) {
  const cached = token ? skillsCache.get(skillsCacheKey(projectId, token)) : undefined;
  const [composerSkills, setComposerSkills] = useState<SkillDefinition[]>(cached?.skills ?? []);
  const [composerRecentSkills, setComposerRecentSkills] = useState<SkillDefinition[]>(() => {
    if (!cached) return [];
    const recentIds = [...new Set(cached.executions.map((item) => item.skillId))];
    return recentIds
      .map((id) => cached.skills.find((skill) => skill.id === id))
      .filter((skill): skill is SkillDefinition => Boolean(skill))
      .slice(0, 2);
  });
  const [composerProjects, setComposerProjects] = useState<ProjectSummary[]>(
    cached?.projects ?? []
  );
  const [skillExecutions, setSkillExecutions] = useState<SkillExecution[]>(
    cached?.executions ?? []
  );
  const [projectArtifacts, setProjectArtifacts] = useState<ArtifactSummary[]>(
    cached?.artifacts ?? []
  );
  const [selectedSkill, setSelectedSkill] = useState<SkillDefinition | null>(null);
  const [skillExecution, setSkillExecution] = useState<SkillExecution | null>(null);
  const [skillLaunching, setSkillLaunching] = useState(false);
  const [skillLaunchError, setSkillLaunchError] = useState<string | null>(null);
  const webSearchAvailable = composerSkills.some((skill) => skill.id === WEB_SEARCH_SKILL_ID);
  const appliedPendingSkillId = useRef<string | null>(null);

  useEffect(() => {
    if (!pendingSkillId || appliedPendingSkillId.current === pendingSkillId) return;
    const definition = composerSkills.find((skill) => skill.id === pendingSkillId);
    if (!definition) return;
    appliedPendingSkillId.current = pendingSkillId;
    setSelectedSkill(definition);
    setSkillExecution(null);
    setSkillLaunchError(null);
  }, [composerSkills, pendingSkillId]);

  useEffect(() => {
    if (!token) return;
    const accessToken = token;
    const controller = new AbortController();
    const { signal } = controller;
    void Promise.allSettled([
      apiRequest<{ data: SkillDefinition[] }>('/skills?limit=100', accessToken, { signal }),
      projectId
        ? apiRequest<{ data: SkillExecution[] }>(
            `/projects/${projectId}/skills/executions?limit=50`,
            accessToken,
            { signal }
          )
        : Promise.resolve({ data: [] as SkillExecution[] }),
      apiRequest<{ data: ProjectSummary[] }>('/projects?limit=100', accessToken, { signal }),
      projectId
        ? apiRequest<{ data: ArtifactSummary[] }>(
            `/projects/${projectId}/artifacts?limit=100`,
            accessToken,
            {
              signal,
            }
          )
        : Promise.resolve({ data: [] as ArtifactSummary[] }),
    ]).then(([catalogResult, executionResult, projectResult, artifactResult]) => {
      if (signal.aborted) return;
      const skills = catalogResult.status === 'fulfilled' ? catalogResult.value.data : null;
      const executions = executionResult.status === 'fulfilled' ? executionResult.value.data : null;
      const projectList = projectResult.status === 'fulfilled' ? projectResult.value.data : null;
      const artifacts = artifactResult.status === 'fulfilled' ? artifactResult.value.data : null;
      if (catalogResult.status === 'rejected' || executionResult.status === 'rejected') {
        const cause =
          executionResult.status === 'rejected'
            ? executionResult.reason
            : catalogResult.status === 'rejected'
              ? catalogResult.reason
              : null;
        setSkillLaunchError(cause instanceof Error ? cause.message : t('skillsLoadError'));
      }
      if (artifacts) {
        setProjectArtifacts((current) => mergeProjectArtifacts(artifacts, current));
      }
      if (!skills || !executions) return;
      const recentIds = [...new Set(executions.map((item) => item.skillId))];
      setComposerSkills(skills);
      setComposerRecentSkills(
        recentIds
          .map((id) => skills.find((skill) => skill.id === id))
          .filter((skill): skill is SkillDefinition => Boolean(skill))
          .slice(0, 2)
      );
      if (projectList) setComposerProjects(projectList);
      setSkillExecutions(executions);
      skillsCache.set(skillsCacheKey(projectId, accessToken), {
        artifacts: artifacts ?? [],
        executions,
        projects: projectList ?? [],
        skills,
      });
    });
    return () => controller.abort();
  }, [projectId, t, token]);

  const activeSkillExecutionId =
    skillExecution?.status === 'pending' || skillExecution?.status === 'running'
      ? skillExecution.id
      : null;
  const latestWebsiteExecution = skillExecutions.find(
    ({ skillId }) => skillId === 'research.website_evidence'
  );
  const activeWebsiteExecutionId =
    latestWebsiteExecution?.status === 'pending' || latestWebsiteExecution?.status === 'running'
      ? latestWebsiteExecution.id
      : null;

  useSkillExecutionUpdates({
    accessToken: token,
    fallbackIntervalMs: 10_000,
    onError: (cause) =>
      setSkillLaunchError(cause instanceof Error ? cause.message : t('skillsLoadError')),
    onReconcile: async (signal) => {
      if (!projectId || !activeSkillExecutionId || !token) return;
      const { data } = await apiRequest<{ data: SkillExecution }>(
        `/projects/${projectId}/skills/executions/${activeSkillExecutionId}`,
        token,
        { signal }
      );
      setSkillExecution(data);
      setSkillExecutions((current) => [
        data,
        ...current.filter((execution) => execution.id !== data.id),
      ]);
    },
    reconcileImmediately: true,
    target: activeSkillExecutionId ? { id: activeSkillExecutionId, scope: 'execution' } : null,
  });

  useSkillExecutionUpdates({
    accessToken: token,
    fallbackIntervalMs: 10_000,
    onError: (cause) =>
      setSkillLaunchError(cause instanceof Error ? cause.message : t('skillsLoadError')),
    onReconcile: async (signal) => {
      if (!projectId || !activeWebsiteExecutionId || !token) return;
      const { data } = await apiRequest<{ data: SkillExecution }>(
        `/projects/${projectId}/skills/executions/${activeWebsiteExecutionId}`,
        token,
        { signal }
      );
      setSkillExecutions((current) => [
        data,
        ...current.filter((execution) => execution.id !== data.id),
      ]);
    },
    reconcileImmediately: true,
    target:
      activeWebsiteExecutionId && activeWebsiteExecutionId !== activeSkillExecutionId
        ? { id: activeWebsiteExecutionId, scope: 'execution' }
        : null,
  });

  async function launchSkill(input: Record<string, unknown>) {
    if (!projectId || !token || !selectedSkill || skillLaunching) return;
    if (
      selectedSkill.id === 'brief.analyze' &&
      !projectArtifacts.some(
        ({ type, status }) =>
          (type === 'brief' || type === 'business_card') &&
          (status === 'approved' || status === 'published')
      )
    ) {
      setSkillLaunchError(t('briefRequiredForAnalysis'));
      return;
    }
    setSkillLaunching(true);
    setSkillLaunchError(null);
    try {
      const response = await apiRequest<{ data: SkillExecution }>(
        `/projects/${projectId}/skills/executions`,
        token,
        {
          method: 'POST',
          body: JSON.stringify({
            skillId: selectedSkill.id,
            skillVersion: selectedSkill.version,
            clientRequestId: crypto.randomUUID(),
            input,
          }),
        }
      );
      setSkillExecution(response.data);
      setSkillExecutions((current) => [response.data, ...current]);
      setComposerRecentSkills((current) =>
        [selectedSkill, ...current.filter((skill) => skill.id !== selectedSkill.id)].slice(0, 2)
      );
    } catch (cause) {
      setSkillLaunchError(cause instanceof Error ? cause.message : t('skillStartError'));
    } finally {
      setSkillLaunching(false);
    }
  }

  function selectSkill(skill: { id: string }) {
    const definition = composerSkills.find((item) => item.id === skill.id);
    if (!definition) return;
    setSelectedSkill(definition);
    setSkillExecution(null);
    setSkillLaunchError(null);
  }

  const reloadProjectArtifacts = useCallback(async () => {
    if (!projectId || !token) return;
    const { data } = await apiRequest<{ data: ArtifactSummary[] }>(
      `/projects/${projectId}/artifacts?limit=100`,
      token
    );
    setProjectArtifacts((current) => {
      const next = mergeProjectArtifacts(data, current);
      const key = skillsCacheKey(projectId, token);
      const cached = skillsCache.get(key);
      if (cached) skillsCache.set(key, { ...cached, artifacts: next });
      return next;
    });
  }, [projectId, token]);

  const reloadSkillExecutions = useCallback(async () => {
    if (!projectId || !token) return;
    const { data } = await apiRequest<{ data: SkillExecution[] }>(
      `/projects/${projectId}/skills/executions?limit=50`,
      token
    );
    setSkillExecutions(data);
    const key = skillsCacheKey(projectId, token);
    const cached = skillsCache.get(key);
    if (cached) skillsCache.set(key, { ...cached, executions: data });
  }, [projectId, token]);

  function rememberProjectArtifact(artifact: ArtifactSummary) {
    setProjectArtifacts((current) => {
      const next = mergeProjectArtifacts(current, [artifact]);
      if (token) {
        const cached = skillsCache.get(skillsCacheKey(projectId, token));
        if (cached) {
          skillsCache.set(skillsCacheKey(projectId, token), { ...cached, artifacts: next });
        }
      }
      return next;
    });
  }

  async function approveProjectArtifact(artifact: ArtifactSummary): Promise<ArtifactSummary> {
    if (!projectId || !token) throw new Error(t('artifactApproveError'));
    const { data } = await apiRequest<{ data: ArtifactSummary }>(
      `/projects/${projectId}/artifacts/${artifact.id}/approve`,
      token,
      {
        method: 'POST',
        body: JSON.stringify({ expectedVersion: artifact.version }),
      }
    );
    rememberProjectArtifact(data);
    return data;
  }

  function clearSkill() {
    setSelectedSkill(null);
    setSkillExecution(null);
    setSkillLaunchError(null);
  }

  return {
    clearSkill,
    composerProjects,
    composerRecentSkills,
    composerSkills,
    approveProjectArtifact,
    launchSkill,
    projectArtifacts,
    reloadProjectArtifacts,
    reloadSkillExecutions,
    rememberProjectArtifact,
    selectSkill,
    selectedSkill,
    skillExecution,
    skillExecutions,
    skillLaunchError,
    skillLaunching,
    webSearchAvailable,
  };
}
